import React from "react";
import {
  Button,
  FormControl,
  FormLabel,
  Select,
  SimpleGrid,
  Skeleton,
  useToast,
} from "@chakra-ui/react";
import { useMutation } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import RiskReviewErrorState from "../components/common/RiskReviewErrorState";
import RiskReviewPageShell from "../components/common/RiskReviewPageShell";
import RiskReviewPanel from "../components/common/RiskReviewPanel";
import { useRiskReviewCreateOptions } from "../hooks/useRiskReviewCreateOptions";
import riskReviewService from "../services/riskReviewService";
import { getRiskReviewErrorMessage } from "../utils/riskReviewFormatters";

const ANY_VALUE = "__ANY__";

const templateMatches = (template, criteria) =>
  Object.entries(criteria).every(([key, value]) => {
    const normalizedValue = value === ANY_VALUE ? "" : value;
    return !normalizedValue || !template[key] || template[key] === normalizedValue;
  });

const templateOptionsFor = (templates, key, criteria = {}) => {
  const matchingTemplates = templates.filter((template) =>
    templateMatches(template, criteria),
  );
  const values = Array.from(
    new Set(matchingTemplates.map((template) => template[key]).filter(Boolean)),
  ).map((value) => ({ label: value, value }));

  const hasAnyTemplate = matchingTemplates.some((template) => !template[key]);
  return hasAnyTemplate ? [{ label: "Any", value: ANY_VALUE }, ...values] : values;
};

const normalizePayload = (payload) =>
  Object.fromEntries(
    Object.entries(payload).map(([key, value]) => [
      key,
      value === ANY_VALUE ? "" : value,
    ]),
  );

const RiskReviewRequestCreatePage = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const [payload, setPayload] = React.useState({
    requestType: "",
    businessUnit: "",
    division: "",
    country: "",
  });
  const optionsQuery = useRiskReviewCreateOptions();
  const options = React.useMemo(() => optionsQuery.data || {}, [optionsQuery.data]);

  React.useEffect(() => {
    if (!optionsQuery.data) return;
    const matchingTemplates = options.templates || [];
    const nextRequestType = payload.requestType || options.requestTypes?.[0]?.value || "";
    const nextBusinessUnits = templateOptionsFor(matchingTemplates, "businessUnit", {
      requestType: nextRequestType,
    });
    const nextBusinessUnit = payload.businessUnit || nextBusinessUnits[0]?.value || "";
    const nextDivisions = templateOptionsFor(matchingTemplates, "division", {
      requestType: nextRequestType,
      businessUnit: nextBusinessUnit,
    });
    const nextDivision = payload.division || nextDivisions[0]?.value || "";
    const nextCountries = templateOptionsFor(matchingTemplates, "country", {
      requestType: nextRequestType,
      businessUnit: nextBusinessUnit,
      division: nextDivision,
    });
    setPayload((current) => ({
      ...current,
      requestType: current.requestType || nextRequestType,
      businessUnit: current.businessUnit || nextBusinessUnit,
      division: current.division || nextDivision,
      country: current.country || nextCountries[0] || "",
    }));
  }, [optionsQuery.data, options, payload.businessUnit, payload.division, payload.requestType]);

  const filteredTemplates = React.useMemo(() => options.templates || [], [options.templates]);
  const businessUnitOptions = React.useMemo(
    () =>
      templateOptionsFor(filteredTemplates, "businessUnit", {
        requestType: payload.requestType,
      }),
    [filteredTemplates, payload.requestType],
  );
  const divisionOptions = React.useMemo(
    () =>
      templateOptionsFor(filteredTemplates, "division", {
        requestType: payload.requestType,
        businessUnit: payload.businessUnit,
      }),
    [filteredTemplates, payload.businessUnit, payload.requestType],
  );
  const countryOptions = React.useMemo(
    () =>
      templateOptionsFor(filteredTemplates, "country", {
        requestType: payload.requestType,
        businessUnit: payload.businessUnit,
        division: payload.division,
      }),
    [filteredTemplates, payload.businessUnit, payload.division, payload.requestType],
  );

  const createMutation = useMutation({
    mutationFn: riskReviewService.createRequest,
    onSuccess: (request) => {
      toast({ title: "Risk Review draft created", status: "success" });
      navigate(`/risk-review/requests/${request._id}`);
    },
    onError: (error) => {
      toast({ title: getRiskReviewErrorMessage(error), status: "error" });
    },
  });

  const update = (key, value) =>
    setPayload((current) => {
      const next = { ...current, [key]: value };
      if (key === "requestType") {
        next.businessUnit = "";
        next.division = "";
        next.country = "";
      }
      if (key === "businessUnit") {
        next.division = "";
        next.country = "";
      }
      if (key === "division") {
        next.country = "";
      }
      return next;
    });
  const renderSelect = ({ keyName, label, items = [], required = false }) => (
    <FormControl isRequired={required}>
      <FormLabel>{label}</FormLabel>
      <Select
        value={payload[keyName]}
        placeholder={items.length ? "Select" : "No tenant options configured"}
        isDisabled={optionsQuery.isLoading || !items.length}
        onChange={(event) => update(keyName, event.target.value)}
      >
        {items.map((item) => (
        <option key={`${keyName}-${item.value}`} value={item.value}>
            {item.label}
          </option>
        ))}
      </Select>
    </FormControl>
  );

  return (
    <RiskReviewPageShell
      title="Create Risk Review"
      description="Choose the business context used to determine the applicable review template."
    >
      {optionsQuery.isError ? (
        <RiskReviewErrorState error={optionsQuery.error} onRetry={optionsQuery.refetch} />
      ) : null}
      <RiskReviewPanel p={5} maxW="920px">
        {optionsQuery.isLoading ? (
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            {Array.from({ length: 4 }).map((_, index) => (
              <Skeleton key={index} height="40px" borderRadius="md" />
            ))}
          </SimpleGrid>
        ) : (
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={4}>
            {renderSelect({
              keyName: "requestType",
              label: "Request Type",
              items: options.requestTypes,
              required: true,
            })}
            {renderSelect({
              keyName: "businessUnit",
              label: "Business Unit",
              items: businessUnitOptions,
              required: true,
            })}
            {renderSelect({
              keyName: "division",
              label: "Division",
              items: divisionOptions,
            })}
            {renderSelect({
              keyName: "country",
              label: "Country",
              items: countryOptions,
            })}
          </SimpleGrid>
        )}
        <Button
          mt={5}
          colorScheme="blue"
          isLoading={createMutation.isPending || optionsQuery.isLoading}
          isDisabled={!payload.requestType}
          onClick={() => createMutation.mutate(normalizePayload(payload))}
        >
          Continue
        </Button>
      </RiskReviewPanel>
    </RiskReviewPageShell>
  );
};

export default RiskReviewRequestCreatePage;
